import React, { useMemo } from 'react';
import { Alert, Box, Link as MuiLink, MenuItem, TextField, Theme } from '@mui/material';
import { BURNABLE_REWARD_TYPES, RewardType } from '@loyalty/shared-common/rewards-api-client';
import { Controller, useFormContext } from 'react-hook-form';
import { Feature, getLocalizedProperty } from '@loyalty/shared-common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useParams } from 'react-router-dom';
import { QlfNumberField, colorPalette } from '@qualifio/ui-commons';
import { faEquals } from '@fortawesome/pro-regular-svg-icons/faEquals';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import { ArchivedCurrencyTooltip } from '../currency/components/archived-currency-tooltip';
import { CurrencyDropdown } from '../currency/components/currency-dropdown';
import { Router } from '../services';
import { SxStyles } from '../shared/utils/types';
import { useCurrencies } from '../currency/CurrenciesContext';
import { useFeatures } from '../features/FeaturesContext';
import { useRewards } from '../rewards/RewardsContext';

const styles: SxStyles = {
  listItemContent: {
    padding: [1, 1],
    '&:last-child': {
      paddingBottom: 1,
    },
  },
};
const useStyles = makeStyles<Theme>(({ breakpoints, spacing }) => {
  return {
    root: {},
    fields: {
      display: 'flex',
      maxWidth: breakpoints.values['sm'],
      marginLeft: '10px',
    },
    equals: {
      textAlign: 'center',
    },
    listItemContent: {
      padding: spacing(1, 1),
      '&:last-child': {
        paddingBottom: spacing(1),
      },
    },
  };
});

export function BurningRulePanelRewards(): React.ReactElement {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const { accountId, id } = useParams<Record<string, string>>();

  const { currencies } = useCurrencies();
  const { rewards } = useRewards();
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext();
  const { features } = useFeatures();
  const { write: burningRuleWrite } = features[Feature.BURNING_RULE];

  const activeRewards = useMemo(
    () =>
      rewards.filter(reward => {
        return !reward.deletedAt && BURNABLE_REWARD_TYPES.includes(reward.type);
      }),
    [rewards],
  );

  const rewardFieldName = 'actions.reward.rewardId';
  const transactionValueFieldName = 'actions.trans.value';
  const transactionCurrencyFieldName = 'actions.trans.currency';
  const currencyFieldValue = watch(transactionCurrencyFieldName);

  const ruleCurrency = useMemo(
    () => currencies.find(currency => currency.id === currencyFieldValue),
    [currencies, currencyFieldValue],
  );

  const rewardOptions = useMemo(() => {
    return activeRewards.map(reward => {
      let language = i18n.language;

      if (!reward.i18nAttributes[language]) {
        language = Object.keys(reward.i18nAttributes).pop() || '';
      }

      const text = getLocalizedProperty(reward, 'name', language);

      return {
        value: reward.id || '',
        text,
      };
    });
  }, [activeRewards, i18n.language]);

  if (rewardOptions.length === 0) {
    return (
      <Alert severity="info">
        <span>
          {t('burning-rules.errors.empty-rewards')}
          <MuiLink
            color={colorPalette.secondary.main}
            component={Link}
            to={Router.getPath('rewards', { accountId: accountId || null, id: id || null })}
          >
            {t('burning-rules.errors.empty-rewards-link')}
          </MuiLink>
        </span>
      </Alert>
    );
  }

  if (!currencies.length) {
    return <></>;
  }

  return (
    <div className={classes.root}>
      <Box className={classes.fields} sx={{ display: 'flex', alignItems: 'flex-start' }}>
        <Controller
          control={control}
          name={rewardFieldName}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              disabled={!burningRuleWrite}
              select
              error={!!error}
              helperText={error?.message}
              id={'select-reward-button-id'}
            >
              {rewardOptions.map(type => (
                <MenuItem key={type.value} value={type.value}>
                  {type.text}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
        <Box className={classes.equals} style={{ padding: '14px', paddingTop: 10, width: '10%' }}>
          <FontAwesomeIcon icon={faEquals} />
        </Box>
        <Box style={{ width: '20%' }} className={classes.fields}>
          <Controller
            control={control}
            name={transactionValueFieldName}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <QlfNumberField
                {...field}
                disabled={!burningRuleWrite}
                error={!!error}
                sx={styles.currencyAmountField}
                helperText={error?.message}
              />
            )}
          />
        </Box>
        <Box
          className={classes.fields}
          id={'currency-dropdown-burnrule-id'}
          style={{ alignItems: 'center' }}
        >
          <Controller
            control={control}
            name={transactionCurrencyFieldName}
            render={({ field: { ref, ...field } }) => (
              <CurrencyDropdown fullWidth {...field} disabled={!burningRuleWrite} />
            )}
          />
          {ruleCurrency?.deletedAt ? <ArchivedCurrencyTooltip sx={styles.listItemContent} /> : null}
        </Box>
      </Box>
    </div>
  );
}
